// AGC: PreventiveMaintenanceCard - Shows standard maintenance intervals by machine type
// AGC: Displays active/inactive intervals with configure button
// AGC: 10h/daily intervals are inactive by default, others are active

import React, { useState, useEffect } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import {
  getMaintenanceStandardsForCategory,
  getIntervalPriorityColor,
  getCategoryDisplayName,
  type MaintenanceInterval,
} from '../../types/maintenanceStandards'
import type { MachineCategory } from '../../types/machine'
import {
  Settings,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Wrench,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'

interface PreventiveMaintenanceCardProps {
  machineId: string
  category: MachineCategory
  currentHours: number
  lastMaintenanceHours?: number
  activeIntervalIds?: string[]  // AGC: IDs of active intervals (from machine data)
  onConfigureClick?: () => void
  onToggleInterval?: (intervalId: string, isActive: boolean) => void
  canEdit?: boolean
}

/**
 * AGC: PreventiveMaintenanceCard - Displays standard maintenance schedule
 * AGC: Features:
 * AGC: - Shows all standard intervals for the machine category
 * AGC: - Indicates which intervals are active/inactive
 * AGC: - Shows maintenance status (due, upcoming, ok)
 * AGC: - Configure button for custom settings
 */
export const PreventiveMaintenanceCard: React.FC<PreventiveMaintenanceCardProps> = ({
  machineId,
  category,
  currentHours,
  lastMaintenanceHours = 0,
  activeIntervalIds,
  onConfigureClick,
  onToggleInterval,
  canEdit = true,
}) => {
  const { t } = useTranslation()
  const [expandedInterval, setExpandedInterval] = useState<string | null>(null)
  const [showAllIntervals, setShowAllIntervals] = useState(false)

  // AGC: Get standard maintenance intervals for this machine category
  const standardIntervals = getMaintenanceStandardsForCategory(category)
  
  // AGC: Number of intervals to show in collapsed view (10h and 50h)
  const COLLAPSED_COUNT = 2
  
  // AGC: Get intervals to display based on expanded state
  const visibleIntervals = showAllIntervals 
    ? standardIntervals 
    : standardIntervals.slice(0, COLLAPSED_COUNT)
  
  // AGC: Check if there are more intervals to show
  const hasMoreIntervals = standardIntervals.length > COLLAPSED_COUNT

  // AGC: Determine which intervals are active
  // AGC: If activeIntervalIds is provided, use it; otherwise use default
  const getIsIntervalActive = (interval: MaintenanceInterval): boolean => {
    if (activeIntervalIds !== undefined) {
      return activeIntervalIds.includes(interval.id)
    }
    return interval.isActiveByDefault
  }

  // AGC: Calculate hours until next maintenance for an interval
  const getMaintenanceStatus = (interval: MaintenanceInterval): {
    status: 'ok' | 'upcoming' | 'due' | 'overdue'
    hoursRemaining: number
  } => {
    const hoursSinceLastMaintenance = currentHours - lastMaintenanceHours
    const hoursRemaining = interval.hours - hoursSinceLastMaintenance
    
    if (hoursRemaining <= 0) {
      return { status: 'overdue', hoursRemaining }
    } else if (hoursRemaining <= 10) {
      return { status: 'due', hoursRemaining }
    } else if (hoursRemaining <= interval.hours * 0.2) {
      return { status: 'upcoming', hoursRemaining }
    }
    return { status: 'ok', hoursRemaining }
  }

  // AGC: Get status badge color
  const getStatusBadgeColor = (status: string): string => {
    switch (status) {
      case 'overdue':
        return 'bg-red-500 text-white'
      case 'due':
        return 'bg-amber-500 text-white'
      case 'upcoming':
        return 'bg-yellow-400 text-gray-900'
      default:
        return 'bg-emerald-500 text-white'
    }
  }

  // AGC: Get status label
  const getStatusLabel = (status: string, hoursRemaining: number): string => {
    switch (status) {
      case 'overdue':
        return `${Math.abs(hoursRemaining).toFixed(0)}h atrasado`
      case 'due':
        return `${hoursRemaining.toFixed(0)}h restantes`
      case 'upcoming':
        return `${hoursRemaining.toFixed(0)}h restantes`
      default:
        return `${hoursRemaining.toFixed(0)}h restantes`
    }
  }

  // AGC: Toggle interval expansion
  const toggleExpand = (intervalId: string) => {
    setExpandedInterval(expandedInterval === intervalId ? null : intervalId)
  }

  // AGC: Count active and inactive intervals
  const activeCount = standardIntervals.filter(getIsIntervalActive).length
  const inactiveCount = standardIntervals.length - activeCount

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* AGC: Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-emerald-500" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Manutenção Preventiva
            </h3>
          </div>
          {onConfigureClick && canEdit && (
            <button
              onClick={onConfigureClick}
              className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
            >
              Configurar
            </button>
          )}
        </div>
        
        {/* AGC: Category and status summary */}
        <div className="flex items-center gap-3 mt-2 text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            Padrão: <span className="font-medium text-gray-900 dark:text-white">{getCategoryDisplayName(category)}</span>
          </span>
          <span className="text-gray-400 dark:text-gray-600">|</span>
          <span className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span className="text-emerald-600 dark:text-emerald-400">{activeCount} ativos</span>
          </span>
          {inactiveCount > 0 && (
            <span className="flex items-center gap-1">
              <XCircle className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">{inactiveCount} inativos</span>
            </span>
          )}
        </div>
      </div>

      {/* AGC: Intervals List - Collapsed by default showing only first 2 */}
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {visibleIntervals.map((interval) => {
          const isActive = getIsIntervalActive(interval)
          const { status, hoursRemaining } = getMaintenanceStatus(interval)
          const isExpanded = expandedInterval === interval.id

          return (
            <div
              key={interval.id}
              className={`${!isActive ? 'opacity-50 bg-gray-50 dark:bg-gray-800/50' : ''}`}
            >
              {/* AGC: Interval Header */}
              <div
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                onClick={() => toggleExpand(interval.id)}
              >
                <div className="flex items-center gap-3 flex-1">
                  {/* AGC: Active/Inactive Toggle */}
                  {canEdit && onToggleInterval && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleInterval(interval.id, !isActive)
                      }}
                      className="flex-shrink-0"
                    >
                      {isActive ? (
                        <ToggleRight className="w-6 h-6 text-emerald-500" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-gray-400" />
                      )}
                    </button>
                  )}

                  {/* AGC: Interval Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${getIntervalPriorityColor(interval.priority)}`}>
                        {interval.hours}h
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white truncate">
                        {interval.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {interval.tasks.length} tarefas
                    </p>
                  </div>
                </div>

                {/* AGC: Status Badge (only for active intervals) */}
                <div className="flex items-center gap-2">
                  {isActive && (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(status)}`}>
                      {getStatusLabel(status, hoursRemaining)}
                    </span>
                  )}
                  {!isActive && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                      Desativado
                    </span>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>

              {/* AGC: Expanded Tasks */}
              {isExpanded && (
                <div className="px-4 pb-3 bg-gray-50 dark:bg-gray-700/30">
                  <ul className="space-y-1.5 ml-9">
                    {interval.tasks.map((task, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
                      >
                        <Wrench className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
                        <span>{task}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* AGC: Warning for daily/10h interval */}
                  {interval.hours <= 10 && !isActive && (
                    <div className="mt-3 ml-9 flex items-start gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-yellow-700 dark:text-yellow-300">
                        Verificações diárias são recomendadas para manter a máquina em bom estado. 
                        Ative este intervalo para receber lembretes.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
        
        {/* AGC: Show more / Show less button */}
        {hasMoreIntervals && (
          <button
            onClick={() => setShowAllIntervals(!showAllIntervals)}
            className="w-full px-4 py-2.5 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-center gap-1"
          >
            {showAllIntervals ? (
              <>
                <ChevronUp className="w-4 h-4" />
                {t('machines.detail.preventive.showLess')}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                {t('machines.detail.preventive.showMore', { count: standardIntervals.length - COLLAPSED_COUNT })}
              </>
            )}
          </button>
        )}
      </div>

      {/* AGC: Footer with current hours info */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4" />
            <span>Horímetro atual:</span>
            <span className="font-semibold text-gray-900 dark:text-white">{currentHours.toFixed(0)}h</span>
          </div>
          {lastMaintenanceHours > 0 && (
            <span className="text-gray-500 dark:text-gray-400">
              Última manutenção: {lastMaintenanceHours.toFixed(0)}h
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default PreventiveMaintenanceCard
